import { useCallback, useEffect, useMemo, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useSession } from '@/auth/SessionContext';
import { PWA_TAB_PERMISSIONS } from '@/auth/permissions';
import {
  AppHeaderActionsHost,
  AppHeaderActionsProvider,
} from '@/components/AppHeaderActions';
import { AppHeaderMenu } from '@/components/AppHeaderMenu';
import { InstallAppPrompt } from '@/components/InstallAppPrompt';
import { IconBell, IconCalendar, IconHome, IconMessages, IconSearch } from '@/components/TabIcons';
import { useAlertsBadgeCount } from '@/hooks/useAlertsBadgeCount';
import { usePendingChatsBadgeCount } from '@/hooks/usePendingChatsBadgeCount';
import { usePullToRefresh } from '@/hooks/usePullToRefresh';
import { resolveBrandLogoSrc } from '@/lib/brandLogo';

export type AppShellOutletContext = {
  hasCalendar: boolean | null;
  hasContactBlacklist: boolean | null;
};

type TabDef = {
  to: string;
  label: string;
  icon: typeof IconHome;
  end?: boolean;
};

function formatBadgeCount(n: number): string {
  if (n > 99) return '99+';
  return String(n);
}

export function AppShell() {
  const { logout, userDisplayName, organizationName, organizationLogoUrl, get, can, permissionsLoaded } =
    useSession();
  const nav = useNavigate();
  const location = useLocation();
  const hideTabbar = /^\/conversations\/[^/]+/.test(location.pathname);
  const ptrEnabled = !location.pathname.startsWith('/conversations');
  const [hasCalendar, setHasCalendar] = useState<boolean | null>(null);
  const [hasContactBlacklist, setHasContactBlacklist] = useState<boolean | null>(null);
  const alertsBadgeCount = useAlertsBadgeCount(get, can, permissionsLoaded);
  const pendingChatsCount = usePendingChatsBadgeCount(get, can, permissionsLoaded);
  const reloadPage = useCallback(() => {
    window.location.reload();
  }, []);
  const { rootRef: mainScrollRef, pullPx, refreshing, armed } = usePullToRefresh({
    enabled: ptrEnabled,
    onRefresh: reloadPage,
  });
  const brandLabel = organizationName?.trim() || 'Empresa';
  const brandLogoSrc = resolveBrandLogoSrc(organizationLogoUrl);
  // onError vía DOM se pierde al re-render (/me cada 60s) y deja el img roto.
  const [displayLogoSrc, setDisplayLogoSrc] = useState(brandLogoSrc);
  useEffect(() => {
    setDisplayLogoSrc(brandLogoSrc);
  }, [brandLogoSrc]);

  useEffect(() => {
    const href = displayLogoSrc;
    const selectors = ['link[rel="icon"]', 'link[rel="apple-touch-icon"]'] as const;
    const prev: { el: HTMLLinkElement; href: string }[] = [];
    for (const sel of selectors) {
      const el = document.querySelector<HTMLLinkElement>(sel);
      if (!el) continue;
      prev.push({ el, href: el.getAttribute('href') || '' });
      el.setAttribute('href', href);
    }
    return () => {
      for (const { el, href: h } of prev) {
        if (h) el.setAttribute('href', h);
      }
    };
  }, [displayLogoSrc]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await get<{ hasCalendar: boolean }>('/api/console/calendario/has-calendar');
        if (!cancelled) setHasCalendar(Boolean(d.hasCalendar));
      } catch {
        if (!cancelled) setHasCalendar(false);
      }
    })();
    (async () => {
      try {
        const d = await get<{ hasPlugin: boolean }>('/api/console/contact-blacklist/has-plugin');
        if (!cancelled) setHasContactBlacklist(Boolean(d.hasPlugin));
      } catch {
        if (!cancelled) setHasContactBlacklist(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [get]);

  const outletCtx = useMemo<AppShellOutletContext>(
    () => ({ hasCalendar, hasContactBlacklist }),
    [hasCalendar, hasContactBlacklist]
  );

  const tabs = useMemo<TabDef[]>(() => {
    const base: TabDef[] = [
      { to: '/', label: 'Inicio', icon: IconHome, end: true },
      { to: '/notifications', label: 'Alertas', icon: IconBell },
      { to: '/conversations', label: 'Chats', icon: IconMessages },
    ];
    if (hasCalendar) {
      base.push({ to: '/calendar', label: 'Calendario', icon: IconCalendar });
      base.push({ to: '/reservas', label: 'Reservas', icon: IconSearch });
    }
    // Hasta que /me responda, no filtrar: si no, el tab bar queda vacío al refrescar.
    if (!permissionsLoaded) return base;
    return base.filter((tab) => {
      const perm = PWA_TAB_PERMISSIONS[tab.to];
      return perm ? can(perm) : true;
    });
  }, [hasCalendar, can, permissionsLoaded]);

  return (
    <AppHeaderActionsProvider>
      <div
        ref={mainScrollRef}
        className={['app-shell', hideTabbar ? 'app-shell--thread' : ''].filter(Boolean).join(' ')}
      >
        <header className="app-shell__header">
          <div className="app-shell__brand">
            <img
              key={brandLogoSrc}
              className="app-shell__brand-logo"
              src={displayLogoSrc}
              alt=""
              width={28}
              height={28}
              decoding="async"
              referrerPolicy="no-referrer"
              onError={() => {
                setDisplayLogoSrc((prev) => (prev === '/logo.png' ? prev : '/logo.png'));
              }}
            />
            <span className="app-shell__brand-name">{brandLabel}</span>
          </div>
          <span className="app-shell__spacer" />
          <AppHeaderActionsHost className="app-shell__header-actions" />
          <AppHeaderMenu
            userLabel={userDisplayName}
            onLogout={() => {
              logout();
              nav('/login', { replace: true });
            }}
          />
        </header>
        <InstallAppPrompt variant="banner" />
        <main
          className={[
            'app-shell__main',
            pullPx > 0 || refreshing ? 'app-shell__main--ptr' : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {ptrEnabled ?
            <div
              className={[
                'app-shell__ptr',
                pullPx > 0 || refreshing ? 'app-shell__ptr--visible' : '',
                armed || refreshing ? 'app-shell__ptr--armed' : '',
                refreshing ? 'app-shell__ptr--refreshing' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              style={{
                height: pullPx > 0 || refreshing ? Math.max(pullPx, refreshing ? 48 : 0) : 0,
                opacity: pullPx > 0 || refreshing ? 1 : 0,
              }}
              aria-live="polite"
              aria-hidden={!(pullPx > 0 || refreshing)}
            >
              <span
                className="app-shell__ptr-spinner"
                style={
                  !refreshing && !armed && pullPx > 0
                    ? { transform: `rotate(${Math.round(pullPx * 4)}deg)` }
                    : undefined
                }
              />
              <span className="app-shell__ptr-label">
                {refreshing ? 'Actualizando…' : armed ? 'Soltá para actualizar' : 'Deslizá para actualizar'}
              </span>
            </div>
          : null}
          <div className="app-shell__inner">
            <Outlet context={outletCtx} />
          </div>
        </main>
        {!hideTabbar ?
          <nav className="app-tabbar" aria-label="Navegación principal">
            {tabs.map(({ to, label, icon: Icon, end }) => {
              const alertsBadge = to === '/notifications' && alertsBadgeCount > 0 ? alertsBadgeCount : 0;
              const chatsBadge = to === '/conversations' && pendingChatsCount > 0 ? pendingChatsCount : 0;
              const badge = chatsBadge || alertsBadge;
              return (
                <NavLink
                  key={to}
                  to={to}
                  end={end}
                  className={({ isActive }) =>
                    ['app-tabbar__item', isActive ? 'app-tabbar__item--active' : ''].filter(Boolean).join(' ')
                  }
                >
                  <span className="app-tabbar__icon-wrap">
                    <Icon aria-hidden className="app-tabbar__icon" />
                    {badge > 0 ?
                      <span
                        className={[
                          'app-tabbar__badge',
                          chatsBadge > 0 ? 'app-tabbar__badge--chats' : '',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                        aria-label={
                          chatsBadge > 0
                            ? `${chatsBadge} chats pendientes`
                            : `${alertsBadge} alertas pendientes`
                        }
                      >
                        {formatBadgeCount(badge)}
                      </span>
                    : null}
                  </span>
                  <span className="app-tabbar__label">{label}</span>
                </NavLink>
              );
            })}
          </nav>
        : null}
      </div>
    </AppHeaderActionsProvider>
  );
}
